import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom'

import { setColumnMapping } from '@disco/state-management/actions/contact-actions'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { getContacts } from '@disco/state-management/selectors/contacts'
import parseCsv, { parseContactsCsv } from '@disco/utils/csv'
import { getExtension } from '@disco/utils/file-utils'

import DragAndDrop from 'components/common/drag-and-drop'
import { Box } from 'components/ds'
import { DiscoModal } from 'components/ui'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import Heading from 'components/ui/heading'
import modalStyles from 'components/utils/modalStyles'

type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux & {
  navigate: NavigateFunction
  params: {
    listId?: string
  }
}
type State = {
  consent: boolean
  error: string | null | undefined
  file: File | null | undefined
}
const CSV_TYPE = 'csv'

const checkCsvFile = file => {
  return getExtension(file.name) === CSV_TYPE
}

const mapStateToProps = state => {
  return {
    columnMapping: getContacts(state).columnMapping,
  }
}

const mapDispatchToProps = {
  setColumnMapping,
  sendGenericError,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

class ContactsImportDialog extends Component<Props, State> {
  state = {
    consent: false,
    error: null,
    file: null,
  }

  componentDidMount() {
    this.props.setColumnMapping(null, null)
  }

  consentCheckboxVisible() {
    return this.props.params.listId
  }

  _onSubmit = () => {
    if (this.consentCheckboxVisible()) {
      if (!this.state.consent) {
        this.setState({
          error:
            'Sorry, we can’t import recipients you don’t have permission to email.',
        })
        return
      }
    }

    if (!this.props.columnMapping) return
    const { listId } = this.props.params
    this.props.navigate(listId ? `/lists/${listId}/import` : `/contacts/import`)
  }
  _onClose = () => {
    const { listId } = this.props.params
    this.props.navigate(listId ? `/lists/${listId}` : '/contacts/list')
  }
  _onFileDrop = files => {
    const file = files[0]
    this.setState(
      {
        file,
      },
      () => {
        if (!checkCsvFile(file)) {
          return this.setState({
            error: this._getFileSelectMessage(),
          })
        }

        parseCsv(file)
          .then(result => {
            this.props.setColumnMapping(parseContactsCsv(result), null)
            this.setState({
              error: null,
            })
          })
          .catch(error => {
            this.props.setColumnMapping(null, error)
            this.setState({
              error: 'Sorry, that file doesn’t appear to be a valid CSV file.',
            })
            this.props.sendGenericError(error)
          })
      },
    )
  }
  _onClear = event => {
    event.preventDefault()
    event.stopPropagation()
    this.props.setColumnMapping(null, null)
    this.setState({
      file: null,
      error: null,
    })
  }
  _getFileSelectMessage = () => {
    const { file } = this.state
    let fileSelectMessage = 'Drop csv file or click to upload'

    if (file) {
      fileSelectMessage = file.name
    }

    if (file && !checkCsvFile(file)) {
      const fileType = getExtension(file.name)
      fileSelectMessage = `.${fileType} is not supported. Please upload a .csv`
    }

    return fileSelectMessage
  }
  _handleConsent = (label, value) => {
    this.setState({
      consent: value,
      error: null,
    })
  }

  render() {
    const { error, file } = this.state
    return (
      <DiscoModal onRequestClose={this._onClose} isOpen style={modalStyles}>
        <div className="Form Form_Channel ContactsImportDialog">
          <Heading size="600">Import contacts from CSV</Heading>
          <div className="form-note form-note--spaced">
            Upload your contacts from a .csv file. In the next step, we&#39;ll
            map the columns in your spreadsheet to the fields we support: Name,
            email, phone number, company, territory, and type.
            <br />
            <br />
            <b>Note: Every contact must have a name and a unique email.</b>
          </div>
          <Box mb="4">
            <DragAndDrop
              onFileDrop={this._onFileDrop}
              onClear={this._onClear}
              errors={[error]}
              files={[error ? null : file]}
              fileIcon={{
                type: 'csv',
                color: '#6B778C',
              }}
            />
          </Box>

          {this.consentCheckboxVisible() && (
            <Checkbox
              label="The people I’m importing have given me permission to email them."
              checked={this.state.consent}
              onChange={this._handleConsent}
            />
          )}
          <div className="Form_ButtonsContainer separated-small">
            <ButtonContainer>
              <Button caption="Cancel" onClick={this._onClose} />
              <Button
                appearance="primary"
                caption="Next"
                disabled={Boolean(!this.props.columnMapping || error)}
                onClick={this._onSubmit}
              />
            </ButtonContainer>
          </div>
        </div>
      </DiscoModal>
    )
  }
}

const ConnectedContactsImportDialog = connector(ContactsImportDialog)

function ContactsImportDialogWithRouter(props: any) {
  const navigate = useNavigate()
  const { listId } = useParams<{ listId?: string }>()
  return (
    <ConnectedContactsImportDialog
      {...props}
      navigate={navigate}
      params={{ listId }}
    />
  )
}

export default ContactsImportDialogWithRouter
